<template>
    <div class="container mt-3">
        <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="mb-0 text-vertex-blue"><strong>Proposal Section Sequence</strong></h5>
            <button class="btn btn-sm btn-outline-info" style="border-radius: 8px!important;" @click="saveSequence" :disabled="isSaving || !initialSections.length">
                <i class="fa-solid fa-save me-1"></i> {{ isSaving ? 'Saving...' : 'Save Sequence' }}
            </button>
        </div>
        <p class="text-muted small mb-4">Drag and drop the sections below to reorder your proposal.
            Click the eye icon to hide or show a section.</p>

        <draggable v-model="sections" group="sections" item-key="section_name" 
            @end="updateSequenceNumbers" animation="200">
            <template #item="{ element, index }">
                <div class="draggable-row d-flex align-items-center mb-2 p-2 border rounded bg-white shadow-sm transition-all"
                    :class="{ 'opacity-50': element.is_hidden }">

                    <i class="fa-solid fa-grip-vertical me-3 text-muted fs-5" title="Drag to reorder"></i>

                    <span class="flex-grow-1 fw-bold"
                        :class="{ 'text-decoration-line-through text-muted': element.is_hidden }"
                        style="font-size: 15px;">
                        {{ formatSectionName(element.section_name) }}
                    </span>

                    <i class="fa-solid fs-5"
                        :class="element.is_hidden ? 'fa-eye-slash text-danger' : 'fa-eye text-success'"
                        style="cursor: pointer;" :title="element.is_hidden ? 'Section is hidden' : 'Section is visible'"
                        @click.stop="toggleVisibility(index)">
                    </i>

                </div>
            </template>
        </draggable>
    </div>
</template>

<script>
import draggable from 'vuedraggable';

export default {
    components: {
        draggable
    },
    props: {
        proposalId: {
            type: String,
            required: true
        },
        initialSections: { type: Array, default: () => [] }
    },
    data() {
        return {
            isSaving: false,
            sections: []
        }
    },
    watch: {
        initialSections: {
            handler(newVal) {
                if (newVal && newVal.length > 0) {
                    this.sections = JSON.parse(JSON.stringify(newVal)); 
                }
            },
            immediate: true
        }
    },
    methods: {
        formatSectionName(name) {
            const names = {
                "CoverPage": "Cover Page",
                "ProductService": "Product & Services",
                "PricingTable": "Pricing Table",
                "ImageGrid": "Image Grid",
                "Testimonials": "Testimonials",
                "SocialImpact": "Social Impact",
                "Commitment": "Commitment to Safety and Compliance",
                "WHS": "WHS Responsibilities",
                "TermsConditions": "Terms & Conditions",
                "NextSteps": "Next Steps"
            };
            return names[name] || name;
        },

        toggleVisibility(index) {
            this.sections[index].is_hidden = !this.sections[index].is_hidden;
        },

        updateSequenceNumbers() {
            this.sections.forEach((section, index) => {
                section.sequence = index + 1;
            });
        },

        async saveSequence() {
            this.isSaving = true;
            try {
                this.updateSequenceNumbers();

                const response = await this.$axios.put(`/proposals/${this.proposalId}/sequence`, this.sections);

                if (response.data.success) {
                    this.$toast.success("Section sequence updated successfully!");
                    this.$emit('sequence-updated', this.sections);
                } else {
                    this.$toast.error(response.data.message || "Failed to save sequence.");
                }
            } catch (error) {
                this.$toast.error("An error occurred while saving the sequence.");
                console.error(error);
            } finally {
                this.isSaving = false;
            }
        }
    }
}
</script>

<style scoped>
/* Target the whole row for the grab cursors instead of just the handle */
.draggable-row {
    cursor: grab;
}

.draggable-row:active {
    cursor: grabbing !important;
}

.transition-all {
    transition: all 0.2s ease;
}

.text-vertex-blue {
    color: #00A0C1;
}
</style>